import React, { useContext, useEffect, useMemo, useState } from 'react';

import ImagePCAScatterPlot from './ImagePCAScatterPlot';
import { cosineSimilarity, cosineToUnit } from './embeddingExplorerUtils.js';
import { loadClusterItems } from './clusterItemsLoader.js';
import { CompareImagePreview } from '../images/CompareImagePreview';
import ImageClusterGallery from '../images/ImageClusterGallery';
import SimilarityBadge from '../common/SimilarityBadge';
import BadgeColorHash from '../common/BadgeColorHash';
import CountryFlag from '../common/CountryFlag';
import { IconButton } from '../common/IconButton';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import { toOriginalImageUrl } from '../fusebox-benchmark/fuseboxBenchmarkUtils';
import TrimSpec from '../lego/trims/TrimSpec';
import { Icon } from '../common/Icon';

const DEFAULT_TOP_K = 8;
const LABEL_COLUMN_BG = '#f1f3f5'; // light gray behind the cluster-label column
// Best-match markers: green for a mutual nearest-neighbour pair, violet for a one-sided best.
const BEST_MATCH_MUTUAL = '#16a34a';
const BEST_MATCH_ONE = '#8b5cf6';

/**
 * Picks two groups of cross-cluster image pairs:
 *   closest  — the ceil(k/2) most similar best-match pairs.
 *   farthest — the floor(k/2) weakest best-match pairs.
 *
 * For every image in poolA, keep only its best image from poolB; for every image in poolB,
 * keep only its best image from poolA. Dedupe reciprocal matches, sort by similarity, then
 * take the top and bottom slices.
 * The bottom slice contains images the other cluster represents poorly — NOT the literally most
 * distant pairs (which would just be unrelated outliers).
 * Both groups are returned in descending similarity so the row reads as one gradient with a gap
 * between them.
 *
 * Each pair carries `bestForA` (b is the A image's single best match) and `bestForB` (a is the B
 * image's single best match); both true = a mutual nearest-neighbour pair.
 */
function closestAndFarthestPairs(poolA, poolB, k) {
  if (!poolA.length || !poolB.length) return [];

  const pairKey = (a, b) => `${a.imgId}|${b.imgId}`;
  const byPair = new Map();
  const markBestMatch = (a, b, sim, side) => {
    const key = pairKey(a, b);
    let entry = byPair.get(key);
    if (!entry) {
      entry = { a, b, sim, bestForA: false, bestForB: false };
      byPair.set(key, entry);
    }
    entry[side] = true;
  };
  const bestForA = poolA.map(() => null);
  const bestForB = poolB.map(() => null);

  for (let i = 0; i < poolA.length; i++) {
    const a = poolA[i];
    for (let j = 0; j < poolB.length; j++) {
      const b = poolB[j];
      const sim = cosineSimilarity(a.vector, b.vector);
      if (!bestForA[i] || sim > bestForA[i].sim) bestForA[i] = { a, b, sim };
      if (!bestForB[j] || sim > bestForB[j].sim) bestForB[j] = { a, b, sim };
    }
  }

  bestForA.forEach((c) => c && markBestMatch(c.a, c.b, c.sim, 'bestForA'));
  bestForB.forEach((c) => c && markBestMatch(c.a, c.b, c.sim, 'bestForB'));

  const candidates = [...byPair.values()];
  candidates.sort((x, y) => y.sim - x.sim); // descending

  const closestCount = Math.min(Math.ceil(k / 2), candidates.length);
  const farthestCount = Math.min(Math.floor(k / 2), Math.max(0, candidates.length - closestCount));
  const farthestSource = farthestCount ? candidates.slice(candidates.length - farthestCount).reverse() : [];

  const closest = candidates
    .slice(0, closestCount)
    .map((c, i) => ({ ...c, kind: 'closest', rank: i + 1 }));

  const farthest = farthestSource
    .map((c, i) => ({ ...c, kind: 'farthest', rank: i + 1 })); // rank 1 = weakest best-match
  farthest.sort((x, y) => y.sim - x.sim); // descending for display continuity

  return [...closest, ...farthest];
}

/** A cluster's label in the grid's left column: id badge + a gallery search button + its model id. */
function ClusterRowLabel({ clusterId, items, modelId, row, onOpen }) {
  return (
    <div
      className="text-center align-self-center"
      style={{ gridColumn: 1, gridRow: row, padding: '0 6px', maxWidth: 160 }}
    >
      {modelId ? <TrimSpec spec="modelId" className='mb-2' value={modelId} /> : null}
      <div className="text-nowrap small">
        <BadgeColorHash id={clusterId} className='mb-2' />
      </div>
      <span className="text-muted">{items.length} imgs</span>
      <IconButton level="primary" icon="search" className="p-0 ml-1 zoom-90" description="Open cluster gallery" onClick={onOpen} />
    </div>
  );
}

/** Country flag + year caption for a thumbnail, overlaid at the bottom (same info as ImageClusterPreview). */
function VehicleCaption({ vehicle }) {
  if (!vehicle || (!vehicle.countryCode && vehicle.year == null)) return null;
  return (
    <div className="cluster-pair-grid__caption small zoom-90">
      <span>
        {vehicle.countryCode ? <CountryFlag countryCode={String(vehicle.countryCode).toLowerCase()} /> : null}
        {vehicle.year != null ? <span className="ml-1">{vehicle.year}</span> : null}
      </span>
    </div>
  );
}

/**
 * One image of a pair. `bestMatchColor` (when set) draws an inset ring marking that this image's
 * single best counterpart in the other cluster is its pair partner (green = mutual best, violet =
 * one-sided). Inset so it never shifts the grid layout.
 */
function PairImageCell({ item, other, row, col, selected = false, divider = false, bestMatchTitle = '' }) {
  return (
    <div
      className="cluster-pair-grid__cell"
      title={bestMatchTitle || undefined}
      style={{
        gridRow: row,
        gridColumn: col,
        position: 'relative',
        ...(divider ? { borderLeft: '2px dashed #cbd5e1', paddingLeft: 4 } : {}),
      }}
    >
      <CompareImagePreview
        url={toOriginalImageUrl(item.imgUrl)}
        compareUrl={toOriginalImageUrl(other.imgUrl)}
        fluidContain
        zoom
        left={row !== 1}
        selected={selected}
        context={item.vehicle}
        title={selected ? `${item.imgUrl} (selected on scatter)` : item.imgUrl}
      />
      <VehicleCaption vehicle={item.vehicle} />
    </div>
  );
}

/**
 * Side-by-side explorer of a candidate cluster pair (dataset review queues):
 *
 *   [ 1/3: PCA scatter of BOTH clusters' embeddings combined, colored by cluster ]
 *   [ 2/3: header row (model / similarity / counter / actions, supplied by the host) +
 *          a pair grid — the k/2 most similar cross-cluster image pairs then the k/2 worst-matched
 *          images (each with its best cross-cluster match), with the pair's similarity badge
 *          between the two images. Every image is a CompareImagePreview against its pair partner. ]
 *
 * Drag-selecting points on the scatter restricts the matching pools: each cluster keeps its
 * selected images (or ALL its images when the selection contains none from that cluster), and
 * the grid rebuilds from what remains.
 *
 * Embeddings are resolved internally (clusterItemsLoader, shared module cache) from the cluster id —
 * no loader prop. Hosts that want instant navigation can warm the same cache via prefetchClusterItems.
 *
 * @param {string} clusterIdA / clusterIdB  the pair
 * @param {string} [modelIdA] / [modelIdB]  shown under each cluster id in the label column
 * @param {React.ReactNode} header  metadata for the min row of the 2/3 column
 * @param {number} [topK=8]  default number of pair columns (user-adjustable in the UI)
 * @param {string} [height]  CSS height of the whole explorer
 */
export default function ClusterPairExplorer({
  clusterIdA,
  clusterIdB,
  modelIdA = '',
  modelIdB = '',
  header = null,
  topK = DEFAULT_TOP_K,
  height = 'calc(100vh - 300px)',
}) {
  const { page } = useContext(LegoAdminPageContext);
  const [loaded, setLoaded] = useState(null); // { idA, idB, itemsA, itemsB }
  const [error, setError] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [k, setK] = useState(topK);
  // imgIds of the pair whose similarity badge is being hovered — ring-highlighted on the scatter.
  const [highlightKeys, setHighlightKeys] = useState(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    setSelectedKeys(new Set());
    Promise.all([loadClusterItems(page, clusterIdA), loadClusterItems(page, clusterIdB)])
      .then(([itemsA, itemsB]) => {
        if (alive) setLoaded({ idA: clusterIdA, idB: clusterIdB, itemsA, itemsB });
      })
      .catch((err) => {
        if (alive) setError(String(err.message || err));
      });
    return () => { alive = false; };
  }, [clusterIdA, clusterIdB, page]);

  // Only trust the loaded items when they belong to the CURRENT pair (a stale previous pair
  // would briefly show wrong matches while the new one loads).
  const current = loaded && loaded.idA === clusterIdA && loaded.idB === clusterIdB ? loaded : null;
  const itemsA = current?.itemsA || [];
  const itemsB = current?.itemsB || [];
  const scatterItems = useMemo(() => [...itemsA, ...itemsB], [itemsA, itemsB]);

  // Stats over each image's PER-IMAGE BEST MATCH (not all n*m pairs): for every image of A its
  // closest image in B, and for every image of B its closest in A — n + m best-match similarities.
  // This measures how well each image is represented in the other cluster, instead of diluting the
  // average with every unrelated cross pair. Whole clusters (ignores the scatter selection).
  // cosineToUnit is monotone-affine, so all stats can be taken on raw cosine and converted once.
  const clusterSimStats = useMemo(() => {
    if (!itemsA.length || !itemsB.length) return null;
    const n = itemsA.length;
    const m = itemsB.length;
    const bestMatches = new Float64Array(n + m).fill(-Infinity); // [0..n) = A's bests, [n..n+m) = B's
    for (let i = 0; i < n; i++) {
      const va = itemsA[i].vector;
      for (let j = 0; j < m; j++) {
        const s = cosineSimilarity(va, itemsB[j].vector);
        if (s > bestMatches[i]) bestMatches[i] = s;
        if (s > bestMatches[n + j]) bestMatches[n + j] = s;
      }
    }
    let sum = 0;
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < bestMatches.length; i++) {
      const s = bestMatches[i];
      sum += s;
      if (s < min) min = s;
      if (s > max) max = s;
    }
    bestMatches.sort(); // typed-array sort is numeric ascending
    const count = bestMatches.length;
    const mid = count >> 1;
    const median = count % 2 ? bestMatches[mid] : (bestMatches[mid - 1] + bestMatches[mid]) / 2;
    return {
      max: cosineToUnit(max),
      avg: cosineToUnit(sum / count),
      median: cosineToUnit(median),
      min: cosineToUnit(min),
    };
  }, [itemsA, itemsB]);

  const openClusterGallery = (clusterId) => {
    page?.openModal(<ImageClusterGallery clusterId={clusterId} />, {
      title: <>Cluster <BadgeColorHash id={clusterId} /></>,
      fullScreenFixed: true,
      oPadding: true,
    });
  };

  // Matching pools: the scatter selection restricts each side to its selected images; a side
  // with nothing selected keeps all its images (so selecting within one cluster still works).
  const pairs = useMemo(() => {
    const selA = itemsA.filter((it) => selectedKeys.has(it.imgId));
    const selB = itemsB.filter((it) => selectedKeys.has(it.imgId));
    const poolA = selectedKeys.size && selA.length ? selA : itemsA;
    const poolB = selectedKeys.size && selB.length ? selB : itemsB;
    return closestAndFarthestPairs(poolA, poolB, Math.max(2, k));
  }, [itemsA, itemsB, selectedKeys, k]);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
        gap: 12,
        height,
        minHeight: 460,
      }}
    >
      <div style={{ minWidth: 0, minHeight: 0 }}>
        {error ? <div className="alert alert-danger">{error}</div> : null}
        {!error && !current ? <div className="alert alert-info">Loading embeddings…</div> : null}
        {!error && current ? (
          <ImagePCAScatterPlot
            items={scatterItems}
            enableDiscriminant
            title={`Both clusters combined (${itemsA.length} + ${itemsB.length} imgs).`}
            selectMode
            selectedKeys={selectedKeys}
            onSelectionChange={setSelectedKeys}
            highlightKeys={highlightKeys}
            markClosestCrossCluster
            defaultShowNnEdges={true}
          />
        ) : null}
      </div>

      <div style={{ display: 'grid', gridTemplateRows: 'min-content minmax(0, 1fr)', minWidth: 0, minHeight: 0 }}>
        <div className="d-flex align-items-center flex-wrap mb-1" style={{ gap: 8 }}>
          {header}
          <label className="small mb-0 ml-auto" title="Total image pairs to show: half the closest pairs, half the worst-matched images (each with its best cross-cluster match)">
            pairs (½ closest, ½ worst-matched){' '}
            <input type="number" min="2" max="20" value={k} style={{ width: 64 }} className="form-control form-control-sm d-inline-block" onChange={(e) => setK(Math.max(2, Math.min(20, parseInt(e.target.value, 10) || topK)))} />
          </label>
          {selectedKeys.size ? (
            <span className="small text-muted">
              {selectedKeys.size} selected on scatter
              <button type="button" className="btn btn-sm btn-outline-secondary py-0 ml-1" onClick={() => setSelectedKeys(new Set())}>
                clear
              </button>
            </span>
          ) : null}
        </div>

        {current && !pairs.length ? (
          <div className="alert alert-secondary">No cross-cluster image pairs to show (selection too narrow?).</div>
        ) : null}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `auto repeat(${Math.max(pairs.length, 1)}, minmax(0, 1fr))`,
            gridTemplateRows: 'minmax(0, 1fr) auto minmax(0, 1fr)',
            border: '1px solid #cbd5e1',
            borderRadius: 4,
            gap: '2px 6px',
            minHeight: 0,
            minWidth: 0,
          }}
        >
          {pairs.length ? (
            <>
              {/* Light-gray underlay behind the whole label column (rendered first so the
                  labels paint on top; spans the row gaps too). */}
              <div style={{ gridColumn: 1, gridRow: '1 / 4', background: LABEL_COLUMN_BG, borderRadius: 4 }} />

              <ClusterRowLabel clusterId={clusterIdA} items={itemsA} modelId={modelIdA} row={1} onOpen={() => openClusterGallery(clusterIdA)} />
              
              <div className="text-center align-self-center" style={{ gridColumn: 1, gridRow: 2, padding: '0 6px' }}>
                {clusterSimStats ? (
                  <div className="text-muted d-inline-block text-right" style={{ lineHeight: 1.4, marginTop: '-40px', marginBottom: '-40px' }}>
                    <div title="Best per-image match: highest similarity any image has to its closest counterpart in the other cluster">
                      max <SimilarityBadge value={clusterSimStats.max} digits={3} />
                    </div>
                    <div className='mt-1 mb-1' title="Median over every image's best match (its closest counterpart in the other cluster)">
                      <strong>median</strong> <SimilarityBadge value={clusterSimStats.median} digits={3} />
                    </div>
                    <div className='mb-1' title="Average over every image's best match (its closest counterpart in the other cluster)">
                      <strong>avg</strong> <SimilarityBadge value={clusterSimStats.avg} digits={3} />
                    </div>
                    <div title="Worst per-image match: the least-represented image's best counterpart in the other cluster">
                      min <SimilarityBadge value={clusterSimStats.min} digits={3} />
                    </div>
                  </div>
                ) : <span className="text-muted small">—</span>}
              </div>
              <ClusterRowLabel clusterId={clusterIdB} items={itemsB} modelId={modelIdB} row={3} onOpen={() => openClusterGallery(clusterIdB)} />
            </>
          ) : null}
          {pairs.map((p, i) => {
            // Dashed divider on the first 'farthest' column marks the closest→worst-matched boundary.
            const divider = p.kind === 'farthest' && (i === 0 || pairs[i - 1].kind !== 'farthest');
            const title = p.kind === 'closest'
              ? `#${p.rank} closest cross-cluster image pair (unit [0,1] similarity)`
              : `#${p.rank} worst-matched image — shown with its best cross-cluster match (unit [0,1] similarity)`;
            // Best-match markers: green ring when mutual (each image is the other's best match),
            // violet on just the side whose best match is its partner.
            const mutual = p.bestForA && p.bestForB;
            const aMarkTitle = p.bestForA ? (mutual ? 'Mutual best match: these two are each other’s closest image' : 'This is the top image’s best match in the other cluster') : '';
            const bMarkTitle = p.bestForB ? (mutual ? 'Mutual best match: these two are each other’s closest image' : 'This is the bottom image’s best match in the other cluster') : '';
            const markerLabel = mutual ? <Icon icon="swap_vert" /> : p.bestForA ? <Icon icon="south" /> : p.bestForB ? <Icon icon="north" /> : null;
            return (
              <React.Fragment key={`${p.a.imgId}|${p.b.imgId}`}>
                {/* Selected = the image itself was scatter-selected; its partner may just be the
                    matched image from the other (unselected) side, so only the former gets the border. */}
                <PairImageCell item={p.a} other={p.b} row={1} col={i + 2} divider={divider} selected={selectedKeys.has(p.a.imgId)} bestMatchTitle={aMarkTitle} />

                <div className="text-center py-2 hover-highlight" title={title}
                  style={{ gridRow: 2, alignSelf: 'center', borderTop: '1px dashed #cbd5e1', borderBottom: '1px dashed #cbd5e1', gridColumn: i + 2, ...(divider ? { borderLeft: '2px dashed #cbd5e1', paddingLeft: 4 } : {}) }}
                  onMouseEnter={() => setHighlightKeys(new Set([p.a.imgId, p.b.imgId]))}
                  onMouseLeave={() => setHighlightKeys(null)}
                >
                  <SimilarityBadge cosine value={p.sim} digits={3} title={title} />
                  {markerLabel ? (
                    <span className="zoom-75 font-weight-bold " title={mutual ? 'Mutual best match' : 'One-sided best match'}>
                      {markerLabel}
                    </span>
                  ) : null}
                </div>

                <PairImageCell item={p.b} other={p.a} row={3} col={i + 2} divider={divider} selected={selectedKeys.has(p.b.imgId)} bestMatchTitle={bMarkTitle} />
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
